import React from 'react';
import { Megaphone, Activity, CheckCircle, TrendingUp } from 'lucide-react';
import { StatCard, StatsGrid } from '../common/DashboardComponents';
import { Card, CardContent } from '@/components/ui/card';

/**
 * Campaign Statistics Component
 * Displays campaign-related statistics
 */
export function CampaignStatistics({ statistics }) {
  if (!statistics) return null;

  const {
    total_campaigns = 0,
    active_campaigns = 0,
    completed_campaigns = 0,
    campaigns_created_this_month = 0,
  } = statistics;

  return (
    <StatsGrid columns={4}>
      <StatCard
        title="Total Campaigns"
        value={total_campaigns}
        subtitle="All time"
        icon={Megaphone}
        color="blue"
      />
      
      <StatCard
        title="Active Campaigns"
        value={active_campaigns}
        subtitle="Currently running"
        icon={Activity}
        color="green"
      />
      
      <StatCard
        title="Completed"
        value={completed_campaigns}
        subtitle="Successfully finished"
        icon={CheckCircle}
        color="purple"
      />
      
      <StatCard
        title="This Month"
        value={campaigns_created_this_month}
        subtitle="New campaigns"
        icon={TrendingUp}
        color="yellow"
      />
    </StatsGrid>
  );
}

/**
 * Campaign Status Breakdown Component
 */
export function CampaignStatusBreakdown({ campaignsByStatus }) {
  if (!campaignsByStatus || Object.keys(campaignsByStatus).length === 0) {
    return null;
  }

  const statuses = Object.entries(campaignsByStatus).map(([status, count]) => ({
    status,
    count,
    percentage: 0
  }));

  const totalCampaigns = statuses.reduce((sum, { count }) => sum + count, 0);
  
  statuses.forEach(item => {
    item.percentage = totalCampaigns > 0 ? ((item.count / totalCampaigns) * 100).toFixed(1) : 0;
  });

  const statusColors = {
    'CREATED': 'bg-gray-500',
    'IN PROGRESS': 'bg-blue-500',
    'LAUNCHED': 'bg-green-500',
    'COMPLETED': 'bg-purple-500',
    'PAUSED': 'bg-yellow-500'
  };

  return (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Campaigns by Status</h3>
        <div className="space-y-4">
          {statuses.map(({ status, count, percentage }) => (
            <div key={status}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">{status}</span>
                <span className="text-sm text-gray-600">
                  {count} ({percentage}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${statusColors[status] || 'bg-gray-500'}`}
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Campaign Type Breakdown Component
 */
export function CampaignTypeBreakdown({ campaignsByType }) {
  if (!campaignsByType || Object.keys(campaignsByType).length === 0) {
    return null;
  }

  const types = Object.entries(campaignsByType).map(([type, count]) => ({
    type,
    count
  }));

  const totalCampaigns = types.reduce((sum, { count }) => sum + count, 0);

  const typeColors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-yellow-500',
    'bg-purple-500',
    'bg-pink-500'
  ];

  return (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Campaigns by Type</h3>
        <div className="space-y-3">
          {types.map(({ type, count }, index) => {
            const percentage = totalCampaigns > 0 ? ((count / totalCampaigns) * 100).toFixed(1) : 0;
            return (
              <div key={type} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${typeColors[index % typeColors.length]}`}></div>
                  <span className="text-sm text-gray-700">{type}</span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {count} ({percentage}%)
                </span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default CampaignStatistics;
